import Text from '@creator/ui/components/Text/Text';
import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import { cn } from '@creator/ui/utils/ui';
import type { OutputBlockData } from '@editorjs/editorjs';
import Icon, { type IconType } from '@src/basic-components/Icon/Icon';
import MenuItem from '@src/basic-components/MenuItem/MenuItem';
import MenuPopover, { type MenuPopoverProps } from '@src/basic-components/MenuPopover/MenuPopover';
import EditFlairModal from '@src/components/EditFlairModal/EditFlairModal';
import ManageDraftPostModal from '@src/components/ManageDraftPostModal/ManageDraftPostModal';
import ModActivityModal from '@src/components/ModActivityModal/ModActivityModal';
import ReportModal from '@src/components/ReportModal/ReportModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { hasFlairs } from '@src/model/token/helpers';
import { ensurePostUrl } from '@src/model/upvote/helpers';
import { canDeletePost, canEditPost, canManageDraftPost, canModeratePost, canPinPost } from '@src/model/upvote/permission';
import { getMyUser, getUserId, isUserJoinedToToken } from '@src/model/user/helpers';
import { isPermissioned } from '@src/model/user/permission';
import { PostStatus } from '@src/store/contentEntities/model';
import { deletePost } from '@src/store/posts/delete';
import { usePost } from '@src/store/posts/hooks/usePost';
import { usePostStats } from '@src/store/posts/hooks/usePostStats';
import { pinPost } from '@src/store/posts/pin';
import { MIN_CHARS_TO_SYNDICATE, syndicatePost } from '@src/store/posts/syndicate';
import { summarizeBlocksContent } from '@src/utils/editorjs-utils/editorjs-utils';
import { abbreviatedFormatter } from '@src/utils/number-utils/number-utils';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { type FC, Fragment, memo, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export interface PostMenuPopoverProps {
    className?: string;
    postId: string;
    tokenName: string;
    hidePinPost?: boolean;
    menuPopoverProps?: Partial<MenuPopoverProps>;
    renderMenuPopoverTrigger?: () => ReactNode;
    onHideTokenClick?: (postId: string, tokenName: string) => void;
    children?: ReactNode;
}

interface MenuItemConfig {
    isVisible: boolean;
    icon: IconType;
    label: string;
    onClick: () => void;
    isLoading?: boolean;
}

const PostMenuPopover: FC<PostMenuPopoverProps> = props => {
    const { hidePinPost = false, postId, tokenName, className, children } = props;

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPinning, setIsPinning] = useState(false);
    const [isSyndicating, setIsSyndicating] = useState(false);

    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const post = usePost(postId);
    const { postStats, loadPostStats } = usePostStats({ postId });

    const token = useStoreState(state => state.token.getToken(tokenName));

    const { handleModal } = useContext<UseModal>(ModalContext);

    useEffect(() => {
        if (!postId || !isMenuVisible) return;
        loadPostStats();
    }, [postId, isMenuVisible]);

    function getBaseClassname(): string {
        return cn('', className);
    }

    function onOpenReportClick(): void {
        setIsMenuVisible(false);
        const options = [t('reportOptions.content.obscene'), t('reportOptions.content.violates'), t('reportOptions.content.suspected'), t('reportOptions.content.spam')];
        handleModal(<ReportModal type="Post" postId={postId} tokenName={tokenName} options={options} />, { title: t('report'), size: 'sm' });
    }

    function onEditFlairClick(): void {
        setIsMenuVisible(false);
        handleModal(<EditFlairModal postId={postId} tokenName={tokenName} />, { size: 'sm' });
    }

    function mapError(error) {
        if (error.message.includes('Too many pinned posts')) return alert(t('tooManyPinnedPostsErrorMessage'));
        if (error.message.includes('banned')) return alert(t('errors.bannedFromCommunity'));

        alert(`${t('generalErrorMessage')} ${error.message}`);
    }

    function onHidePostSelectReason(): void {
        // No need to select a reason if moderator is the publisher
        if (post?.publisherName === getMyUser()?.displayName) {
            onDelete(true, '', '');
            return;
        }
        const options = [t('modActivities.hidePost.spam'), t('modActivities.hidePost.hateful'), t('modActivities.hidePost.nudity')];
        handleModal(<ModActivityModal options={options} onSendReason={onHidePost} subTitle={t('modActivities.modalTitle', { action: t('hide'), entity: 'post' })} />, {
            title: t('modActivities.actions.hidePost'),
            size: 'sm',
        });
    }

    function onManageDraftPostClick(): void {
        handleModal(<ManageDraftPostModal postId={postId} tokenName={tokenName} />, { size: 'md' });
    }

    function onHidePost(reason?: string, reasonText?: string): Promise<void> {
        return onDelete(true, reason, reasonText);
    }

    async function onDelete(isDelete: boolean, reason?: string, reasonText?: string): Promise<void> {
        if (!post) return;

        setIsDeleting(true);

        try {
            await deletePost({
                hidden: isDelete,
                postId: post.id,
                tokenName: post.tokenName,
                userId: getUserId() ?? '',
                reason: reason ? reason : '',
                reasonText: reasonText ? reasonText : '',
            });
            setIsMenuVisible(false);
            setIsDeleting(false);
        } catch (error) {
            setIsMenuVisible(false);
            setIsDeleting(false);
            mapError(error);
        }
    }

    async function onPin(isPin: boolean): Promise<void> {
        if (!post) return;

        setIsPinning(true);

        try {
            await pinPost({
                postId: post.id,
                tokenName: post.tokenName,
                userId: getUserId() ?? '',
                isPinned: isPin,
            });
            setIsPinning(false);
            setIsMenuVisible(false);
        } catch (error) {
            setIsPinning(false);
            setIsMenuVisible(false);
            mapError(error);
        }
    }
    async function onSyndicatePost(syndicate: boolean): Promise<void> {
        if (!post) return;

        setIsSyndicating(true);

        try {
            await syndicatePost({
                postId: post.id,
                tokenName: post.tokenName,
                userId: getUserId() ?? '',
                syndicate,
            });
            setIsSyndicating(false);
            setIsMenuVisible(false);
        } catch (error) {
            setIsSyndicating(false);
            setIsMenuVisible(false);
            mapError(error);
        }
    }

    // Memoized menu items configuration for stable icon rendering
    const menuItems = useMemo((): MenuItemConfig[] => {
        if (!post || !token) return [];

        const { blocks } = post.postContent ? JSON.parse(post.postContent) : { blocks: [] };
        const syndicate = !post.isSyndicated;
        const textContentLength = summarizeBlocksContent(blocks as OutputBlockData[]).length;

        const isDraft = post.status === PostStatus.DRAFT || post.status === PostStatus.PUBLISHED_SCHEDULED;

        // Check if post is in a "hidden" state (deleted but not pending/scheduled/draft)
        const isHidden =
            post.status !== PostStatus.PUBLISHED &&
            post.status !== PostStatus.PUBLISHED_UNLISTED &&
            post.status !== PostStatus.PENDING &&
            post.status !== PostStatus.PUBLISHED_SCHEDULED &&
            post.status !== PostStatus.DRAFT;

        return [
            // Pin/Unpin Post
            {
                isVisible: !hidePinPost && post.status === 0 && canPinPost(post.id),
                icon: (post.isPinned ? 'unpin' : 'pin') as IconType,
                label: post.isPinned ? t('unPinPostLabel') : t('pinPostLabel'),
                onClick: () => onPin(!post.isPinned),
                isLoading: isPinning,
            },
            // Syndicate/Desyndicate Post
            {
                isVisible: post.status === 0 && canModeratePost(postId, tokenName) && post.kind === 'story' && (!syndicate || textContentLength >= MIN_CHARS_TO_SYNDICATE),
                icon: 'paperClip' as IconType,
                label: syndicate ? t('syndicatePostLabel') : t('desyndicatePostLabel'),
                onClick: () => onSyndicatePost(syndicate),
                isLoading: isSyndicating,
            },
            // Edit Published / Hidden Post
            {
                isVisible: canEditPost(postId, tokenName),
                icon: 'edit' as IconType,
                label: t('editPostLabel'),
                onClick: () => navigate(`/bbs/${tokenName}/posts/create/${postId}`),
            },
            // Manage Draft/Scheduled Post
            {
                isVisible: canManageDraftPost(postId, tokenName) && isDraft,
                icon: 'scheduleClock' as IconType,
                label: t('postMenuPopover.manageDraftPostLabel'),
                onClick: onManageDraftPostClick,
            },
            // Hide/Unhide Post
            {
                isVisible: !post.isPinned && canDeletePost(post.id) && (post.status === PostStatus.PUBLISHED || post.status === PostStatus.PUBLISHED_UNLISTED || isHidden),
                icon: (isHidden ? 'show' : 'hide') as IconType,
                label: isHidden ? t('unhidePostLabel') : t('hidePostLabel'),
                onClick: isHidden ? () => onDelete(false) : onHidePostSelectReason,
                isLoading: isDeleting,
            },
            // Edit Flair
            {
                isVisible: hasFlairs(token) && canEditPost(postId, tokenName),
                icon: 'tag' as IconType,
                label: t('postMenuPopover.editFlair'),
                onClick: onEditFlairClick,
            },
            // Hide Token
            {
                isVisible: !!props.onHideTokenClick && isLoggedIn,
                icon: 'hide' as IconType,
                label: t('postMenuPopover.hideToken'),
                onClick: () => props.onHideTokenClick?.(postId, tokenName),
            },
            // Report Post
            {
                isVisible: isLoggedIn && !isDraft && !isHidden,
                icon: 'error' as IconType,
                label: t('report'),
                onClick: onOpenReportClick,
            },
        ].filter(item => item.isVisible);
    }, [post, token, hidePinPost, isPinning, isSyndicating, isDeleting, isLoggedIn, postId, tokenName, props.onHideTokenClick, t]);

    function renderMenuItem(item: MenuItemConfig, index: number) {
        return (
            <MenuItem key={index} onClick={item.onClick} isLoading={item.isLoading}>
                <Icon svgFill={false} name={item.icon} className="mr-2 text-xl" />
                <Text>{item.label}</Text>
            </MenuItem>
        );
    }

    function onMenuPopoverTriggerClick(e: React.MouseEvent<HTMLElement>): void {
        e.stopPropagation();
        e.preventDefault();

        setIsMenuVisible(!isMenuVisible);
    }

    function renderMenuPopoverTrigger() {
        const { renderMenuPopoverTrigger } = props;

        return (
            <div className="cursor-pointer" onClick={onMenuPopoverTriggerClick}>
                {!renderMenuPopoverTrigger && <Icon name="dotsVertical" />}
                {renderMenuPopoverTrigger?.()}
            </div>
        );
    }

    function renderStats() {
        const totalViews = postStats ? abbreviatedFormatter(postStats.totalViews) : '--';
        return (
            <div className="px-4 py-2 text-sm text-gray-500">
                <Text className="text-gray-500">{t('postMenuPopover.totalViews', { totalViews })}</Text>
            </div>
        );
    }

    function renderMenuItems() {
        return (
            <div className={`${isMenuVisible ? 'block' : 'hidden'}`}>
                {children}
                {menuItems.map(renderMenuItem)}
                {renderStats()}
            </div>
        );
    }

    function renderContent() {
        const { menuPopoverProps = {} } = props;

        const _props = mergeProps<[MenuPopoverProps, Partial<MenuPopoverProps>]>(
            {
                className: getBaseClassname(),
                renderTrigger: renderMenuPopoverTrigger,
                onClickOutside: () => setIsMenuVisible(false),
                popoverProps: {
                    className: 'overflow-visible',
                    isVisible: isMenuVisible,
                },
            },
            menuPopoverProps
        );

        return <MenuPopover {..._props}>{renderMenuItems()}</MenuPopover>;
    }

    return renderContent();
};

export default memo(PostMenuPopover, propsEqualityFn);
